import {
  type ColumnsType,
  CountAmountCell,
  Flex,
  Table,
  Text,
  theme,
} from '@/components';
import { PublishedProjectMutableGuard } from '@/features/authentication/_components/PublishedProjectMutableGuard';
import type {
  FundBusinessModel,
  FundBusinessRowModel,
} from '@/features/project/projects/detail/fund-bussiness/_model/fund-business';
import { useLocaledAmount } from '@/providers';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import {
  MIN_YEAR_BOUNDARY,
  getInitialValueOfCalculatedInvestmentBalance,
} from '../_utils/form-control';
import { FundBusinessTableActions } from './FundBusinessTableActions';

type Props = {
  fundBusiness: z.infer<typeof FundBusinessModel>;
  fiscalYear: number;
};

export const FundBusinessTable: FC<Props> = ({ fundBusiness, fiscalYear }) => {
  const { t } = useTranslation();
  const { unit } = useLocaledAmount();
  const { token } = theme.useToken();

  const isOthers = fundBusiness.businessForm === 'others';
  const isSubsidy = fundBusiness.businessForm === 'subsidy';

  const getDisabledStyle = (disabled: boolean) => {
    if (disabled) {
      return {
        style: {
          backgroundColor: token.colorBgContainerDisabled,
        },
      };
    }
    return {};
  };

  const minYear = fiscalYear - MIN_YEAR_BOUNDARY;
  const yearAmountInfos = fundBusiness.performances[0]?.yearAmountInfos ?? [];

  const yearColumns: ColumnsType<z.infer<typeof FundBusinessRowModel>> =
    yearAmountInfos.map((_, columnIndex) => {
      const year = minYear + columnIndex;
      const isEstimatedYear = year >= fiscalYear;
      const isEstimatedYearOverFiscalYear = year > fiscalYear;

      return {
        title:
          isSubsidy && isEstimatedYearOverFiscalYear
            ? t('estimate-over-current-fiscal-year-with-year', { year })
            : isEstimatedYear
              ? t('estimate-with-year', { year })
              : year,
        dataIndex: 'yearAmountInfos',
        width: isSubsidy ? '12.5%' : '15%',
        onCell: (record, rowIndex) => {
          const yearAmountInfo = record.yearAmountInfos[columnIndex];

          return getDisabledStyle(
            !yearAmountInfo?.amountInfo ||
              (isEstimatedYear && !isSubsidy && !isOthers && rowIndex === 0),
          );
        },
        render: (yearAmountInfos, _record, rowIndex) => {
          const amountInfo = yearAmountInfos[columnIndex].amountInfo;

          if (
            !amountInfo ||
            (isEstimatedYear && !isSubsidy && !isOthers && rowIndex === 0)
          ) {
            return;
          }

          return (
            <CountAmountCell
              amount={amountInfo.amount}
              count={amountInfo.count}
              unit={unit}
            />
          );
        },
      };
    });

  const dataSource = getInitialValueOfCalculatedInvestmentBalance({
    fundBusiness,
    fiscalYear,
  })?.performances;

  return (
    <Flex vertical gap={12}>
      <Flex align="center" justify="space-between">
        <Text text={fundBusiness.title} />
        <PublishedProjectMutableGuard>
          <FundBusinessTableActions fundBusinessId={fundBusiness.id} />
        </PublishedProjectMutableGuard>
      </Flex>
      <Table
        bordered
        pagination={false}
        rowKey="id"
        tableLayout="fixed"
        dataSource={dataSource}
        columns={[
          {
            title: isSubsidy
              ? t('fiscal-year-of-issuance-decision')
              : t('actual-results-and-balance'),
            key: 'title',
            dataIndex: 'title',
            width: isSubsidy ? 120 : 280,
            render: (title, _, index) => {
              if (isSubsidy) {
                const year = minYear + index;
                const isEstimatedYear = year >= fiscalYear;

                return isEstimatedYear
                  ? t('estimate-with-year', { year })
                  : t('fund-business-with-year', { year });
              }

              return title;
            },
            rowScope: 'row',
          },
          ...(isSubsidy
            ? // 補助等に関する交付決定実績
              ([
                {
                  title: t('decided-amount-info'),
                  children: [
                    {
                      title: t('fund-business'),
                      key: 'fund-business',
                      dataIndex: 'decidedAmountInfo',
                      width: isSubsidy ? '12.5%' : '15%',
                      onCell: (_record, index) => {
                        const year = minYear + (index ?? 0);
                        const isEstimatedYear = year >= fiscalYear;

                        return getDisabledStyle(isEstimatedYear);
                      },
                      render: (decidedAmountInfo, _record, index) => {
                        const year = minYear + index;
                        const isEstimatedYear = year >= fiscalYear;

                        if (!decidedAmountInfo || isEstimatedYear) {
                          return null;
                        }

                        return (
                          <CountAmountCell
                            amount={decidedAmountInfo.amount}
                            count={decidedAmountInfo.count}
                            unit={unit}
                          />
                        );
                      },
                    },
                    {
                      title: t('estimate'),
                      key: 'estimate',
                      dataIndex: 'estimatedAmountInfo',
                      width: isSubsidy ? '12.5%' : '15%',
                      render: (estimatedAmountInfo) => {
                        if (!estimatedAmountInfo) {
                          return;
                        }

                        return (
                          <CountAmountCell
                            amount={estimatedAmountInfo.amount}
                            count={estimatedAmountInfo.count}
                            unit={unit}
                          />
                        );
                      },
                    },
                  ],
                },
                {
                  title: t('expenditure-year'),
                  children: yearColumns,
                },
              ] satisfies ColumnsType<z.infer<typeof FundBusinessRowModel>>)
            : yearColumns),
        ]}
      />
    </Flex>
  );
};
